import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { ArrowRight, ArrowLeft, X, Sparkles } from 'lucide-react';

const MASCOT = '/operio-mascot-3d.png';

const STEPS = [
  {
    title: 'Hej och välkommen till Operio!',
    body: 'Jag är Operio — din AI-assistent. Jag följer dig genom appen och kan svara på frågor när du klickar på mig i hörnet. Den här rundturen tar 60 sekunder.',
    highlight: 'mascot',
  },
  {
    title: 'Dashboarden — din startpunkt',
    body: 'Här ser du allt på en gång: aktiva projekt, väntande tidrapporter, fakturor och påminnelser. De tre orange knapparna högst upp låter dig starta AI-beräkningar direkt: material, arbete eller tid.',
    highlight: 'dashboard',
  },
  {
    title: 'NYTT: Snabb AI-totalberäkning',
    body: 'Under de tre knapparna finns en lång svart knapp "Totalberäkning". Den räknar ut material, arbete OCH tid på en gång — utan att du behöver skapa ett projekt först. Du kan spara resultatet i Snabb-arkivet och förvandla det till ett riktigt projekt när du vill.',
    highlight: 'totalcalc',
  },
  {
    title: 'Projekt är hjärtat i Operio',
    body: 'Skapa ett projekt och lägg in plats, budget och anställda. Inne i projektet hittar du tidrapporter, ÄTA, materialplan, geofence, kundåtkomst — allt på samma ställe.',
    highlight: 'projects',
  },
  {
    title: 'Tid, ÄTA och löner — automatiskt',
    body: 'Dina arbetare stämplar in via mobilen (med geofence-kontroll). Tidrapporter dyker upp här — godkänn dem så genereras lönen och fakturan automatiskt. Arbetare kan nu också ha en privat timer för personlig spårning (du som admin ser sammanställningen).',
    highlight: 'time',
  },
  {
    title: 'Intern chatt — alla kollegor',
    body: 'Under Internal Chat kan du och alla anställda prata med varandra. Klicka på + för att skapa en gruppchatt (endast du som admin kan skapa grupper). Workers kan starta 1-1-chatt med vilken kollega som helst.',
    highlight: 'chat',
  },
  {
    title: 'Maskiner med smarta påminnelser',
    body: 'Under Maskiner kan du lägga in service, besiktning, certifikat och försäkringar. AI:n påminner dig via push-notiser 30, 10, 7, 3, 2, 1 dag innan + på dagen. När det är klart kan du direkt sätta nästa förfallodatum.',
    highlight: 'machines',
  },
  {
    title: 'AI-bokföring som sköter sig själv',
    body: 'Vår "Auto-Bokföring" läser fakturor och tidrapporter och bokför enligt BAS-kontoplan. Du verifierar bara med ett klick. Inga fler kvällar med pappersarbete.',
    highlight: 'accounting',
  },
  {
    title: 'Klart! Sätt igång.',
    body: 'Du klarar resten själv — men oroa dig inte: när du öppnar en ny funktion första gången tittar jag fram och förklarar vad du kan göra där. Lycka till!',
    highlight: 'mascot',
  },
];

const ONBOARDING_KEY = (userId) => `operio-onboarding-done-v2-${userId}`;

const OnboardingTour = () => {
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    if (localStorage.getItem(ONBOARDING_KEY(user.id))) return;
    // Wait a beat after login to avoid colliding with toasts / layout shift
    const t = setTimeout(() => setVisible(true), 1000);
    return () => clearTimeout(t);
  }, [user]);

  const dismiss = () => {
    if (user) localStorage.setItem(ONBOARDING_KEY(user.id), '1');
    setVisible(false);
  };

  if (!visible || !user) return null;
  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;
  const isFirst = step === 0;

  return (
    <div
      className="fixed inset-0 z-[130] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4 py-6"
      data-testid="onboarding-tour"
    >
      <div className="max-w-lg w-full bg-card rounded-3xl shadow-2xl overflow-hidden relative" onClick={(e) => e.stopPropagation()}>
        {/* Mascot floating top */}
        <div className="bg-gradient-to-b from-[#E8613A]/15 to-transparent pt-8 pb-2 flex justify-center relative">
          <button
            onClick={dismiss}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-card hover:bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
            data-testid="onboarding-skip-btn"
            aria-label="Hoppa över"
          >
            <X size={14} />
          </button>
          <div className="relative">
            <div className="absolute inset-0 bg-[#E8613A]/20 blur-2xl rounded-full scale-110" />
            <img src={MASCOT} alt="Operio" className="relative w-28 h-28 drop-shadow-2xl animate-bounce" style={{ animationDuration: '3s' }} />
          </div>
        </div>

        {/* Content */}
        <div className="px-8 py-6 text-center">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#E8613A]/10 text-[#E8613A] rounded-full mb-3">
            <Sparkles size={11} />
            <span className="text-[10px] font-bold uppercase tracking-widest">Steg {step + 1} av {STEPS.length}</span>
          </div>
          <h2
            className="text-xl sm:text-2xl font-bold tracking-tight text-foreground"
            style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
            data-testid="onboarding-title"
          >
            {current.title}
          </h2>
          <p className="text-sm text-muted-foreground mt-3 leading-relaxed" data-testid="onboarding-body">
            {current.body}
          </p>
        </div>

        {/* Progress dots */}
        <div className="px-8 pb-4 flex items-center justify-center gap-1.5">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === step ? 'bg-[#E8613A] w-6' : i < step ? 'bg-[#E8613A]/40 w-1.5' : 'bg-muted w-1.5'
              }`}
            />
          ))}
        </div>

        {/* Actions */}
        <div className="px-6 pb-6 pt-2 flex items-center justify-between gap-3 border-t border-border bg-muted/30 -mx-px">
          <button
            onClick={dismiss}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            data-testid="onboarding-skip-bottom-btn"
          >
            Hoppa över rundturen
          </button>
          <div className="flex items-center gap-2">
            {!isFirst && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setStep(s => s - 1)}
                data-testid="onboarding-prev-btn"
                className="h-9 px-4 rounded-full"
              >
                <ArrowLeft size={14} />
              </Button>
            )}
            <Button
              onClick={() => (isLast ? dismiss() : setStep(s => s + 1))}
              className="bg-[#E8613A] hover:bg-[#D4532F] text-white h-9 px-5 rounded-full text-xs font-semibold"
              data-testid="onboarding-next-btn"
            >
              {isLast ? 'Klar — sätt igång!' : <>Nästa <ArrowRight size={14} className="ml-1.5" /></>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;
